/**
 * The customer's own page, at /welcome/:token. No account, no sign-in.
 *
 * Everything here used to be read off a phone call and typed into the console
 * by us. One wrong character in a signatory's address sends a contract nowhere
 * and reports nothing. The people who know these answers are the ones being
 * asked.
 *
 * WRITTEN FOR SOMEBODY WHO HAS NEVER SEEN THIS PRODUCT. No jargon, every
 * section says why it is asked, and nothing is required to save — a champion
 * who does not yet know who signs can fill in the rest and come back. Only
 * submitting needs it all.
 *
 * Price appears nowhere. They tell us how many locations they have; what it
 * costs is a conversation, not a form field.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { call, isCallFailure } from '../../lib/callable';
import { Wordmark } from '../../shared/Wordmark';
import { EMAIL_MAX, JOB_TITLE_MAX, ORG_NAME_MAX, PERSON_NAME_MAX, PHONE_MAX } from '../../types/fieldLimits';
import { ORGANIZATION_TYPES, type OrganizationType } from './organizationTypes';
import page from '../../shared/Page.module.css';
import own from './Intake.module.css';

interface Person { name?: string; email?: string; title?: string; phone?: string }
interface Answers {
  /** Who is filling this in, which is rarely one of the four people below. */
  submitter?: { name?: string; email?: string };
  organization?: {
    legalName?: string; address?: string; locationCount?: number | null;
    organizationType?: OrganizationType | ''; targetGoLiveDate?: string;
  };
  champion?: Person; signatory?: Person; billing?: Person; systemAdmin?: Person;
  requirements?: {
    poNumber?: string; poRequired?: boolean | null; needsBaa?: boolean | null;
    needsSecurityReview?: boolean | null; vendorPortal?: string; notes?: string;
  };
}
interface Form {
  orgName: string;
  answers: Answers;
  outstanding: string[];
  complete: boolean;
  submittedAt: string | null;
  /** Which of our documents can be downloaded here. */
  w9Available?: boolean;
  coiAvailable?: boolean;
}

const OUR_DOCS = {
  w9: { title: 'Our W-9', why: 'Your accounts payable will need it to set us up as a supplier.', button: 'Download our W-9' },
  coi: { title: 'Our certificate of insurance', why: 'For your supplier file, if your process asks for one.', button: 'Download our certificate of insurance' },
} as const;

/**
 * Our W-9 and certificate of insurance, for their accounts payable. Supplier
 * setup cannot start without them, so they are offered here rather than
 * waiting on an email from us. The file downloads; the link lasts ten minutes.
 */
function OurDocDownload({ token, kind }: { token: string; kind: keyof typeof OUR_DOCS }) {
  const doc = OUR_DOCS[kind];
  const get = useMutation({
    mutationFn: () => call<{ token: string; kind: string }, { url: string }>('getIntakeCompanyDoc', { token, kind }),
    onSuccess: (r) => { window.location.href = r.url; },
  });
  return (
    <div className={own.field}>
      <strong>{doc.title}</strong>
      <span className={own.hint}>
        {doc.why} Download it here and pass it on, or upload it to your supplier portal.
      </span>
      <div style={{ marginTop: 'var(--sp-2)' }}>
        <button type="button" className={page.btn} disabled={get.isPending} onClick={() => get.mutate()}>
          {get.isPending ? 'Preparing…' : doc.button}
        </button>
      </div>
      {get.error && (
        <span className={own.hint} role="alert">
          {isCallFailure(get.error) ? get.error.message : 'It could not be downloaded. Ask your StandingDocs contact for it.'}
        </span>
      )}
    </div>
  );
}

function OurDocs({ token, data }: { token: string; data: Form }) {
  return (
    <>
      {data.w9Available && <OurDocDownload token={token} kind="w9" />}
      {data.coiAvailable && <OurDocDownload token={token} kind="coi" />}
    </>
  );
}

const PEOPLE = [
  {
    key: 'champion' as const,
    title: 'Who we will be dealing with',
    why: 'The person we talk to day to day. Usually whoever owns policies — a compliance '
      + 'officer, quality director or accreditation coordinator.',
  },
  {
    key: 'signatory' as const,
    title: 'Who signs the agreement',
    why: 'Whoever can commit your organization to an agreement. Often the CFO or a facility '
      + 'administrator, and often not the person filling this in. We send the paperwork '
      + 'straight to them, so getting this right saves the most time.',
  },
  {
    key: 'billing' as const,
    title: 'Where the invoice goes',
    why: 'Accounts payable, or whoever processes supplier invoices. The invoice goes here '
      + 'and nowhere else.',
  },
  {
    key: 'systemAdmin' as const,
    title: 'Who will administer StandingDocs',
    why: 'One person at your organization sets up staff and sign-in. They are the only one '
      + 'who needs an account to begin with, and they will be asked to set up an '
      + 'authenticator app on their phone.',
  },
];

export function IntakePage() {
  const { token = '' } = useParams();
  const [answers, setAnswers] = useState<Answers>({});
  const [saved, setSaved] = useState(false);

  const form = useQuery({
    queryKey: ['intake', token],
    queryFn: () => call<{ token: string }, Form>('getIntakeForm', { token }),
    retry: 1,
  });

  // Fill the fields ONCE, from whatever was saved before. The effect this
  // replaces refilled them after every save's refetch, so anything typed
  // while a save was in flight could be overwritten by the older answers.
  const [filled, setFilled] = useState(false);
  if (form.data && !filled) {
    setFilled(true);
    setAnswers(form.data.answers ?? {});
  }

  const save = useMutation({
    mutationFn: () => call('saveIntakeForm', { token, answers }),
    onSuccess: () => { setSaved(true); void form.refetch(); },
  });
  const submit = useMutation({
    mutationFn: async () => {
      await call('saveIntakeForm', { token, answers });
      return call('submitIntakeForm', { token });
    },
    onSuccess: () => void form.refetch(),
  });

  const failure = [save.error, submit.error].find(Boolean);
  const busy = save.isPending || submit.isPending;

  const setPerson = (role: 'champion' | 'signatory' | 'billing' | 'systemAdmin',
    field: keyof Person, value: string) => {
    setSaved(false);
    setAnswers((a) => ({ ...a, [role]: { ...a[role], [field]: value } }));
  };
  const setOrg = (field: 'legalName' | 'address' | 'organizationType' | 'targetGoLiveDate', value: string) => {
    setSaved(false);
    setAnswers((a) => ({ ...a, organization: { ...a.organization, [field]: value } }));
  };

  const setSubmitter = (field: 'name' | 'email', value: string) => {
    setSaved(false);
    setAnswers((a) => ({ ...a, submitter: { ...a.submitter, [field]: value } }));
  };
  const setReq = (field: string, value: string | boolean | null) => {
    setSaved(false);
    setAnswers((a) => ({ ...a, requirements: { ...a.requirements, [field]: value } }));
  };

  if (form.isLoading) {
    return <div className={own.page}><p className={page.itemMeta}>Opening your form…</p></div>;
  }

  if (form.error) {
    return (
      <div className={own.page}>
        <div className={own.card}>
          <Wordmark />
          <h1 className={page.title}>This link is no longer valid</h1>
          <p className={page.itemMeta}>
            {isCallFailure(form.error) ? form.error.message
              : 'Ask your StandingDocs contact for a new one.'}
          </p>
        </div>
      </div>
    );
  }

  if (form.data?.submittedAt) {
    return (
      <div className={own.page}>
        <div className={own.card}>
          <Wordmark />
          <h1 className={page.title}>Thank you</h1>
          <p>
            We have everything we need for {form.data.orgName}. Your agreement and order form
            will go to {answers.signatory?.name || 'your signatory'} shortly.
          </p>
          <p className={page.itemMeta}>
            If something needs changing, reply to the email that brought you here.
          </p>
          <OurDocs token={token} data={form.data} />
        </div>
      </div>
    );
  }

  const person = (role: 'champion' | 'signatory' | 'billing' | 'systemAdmin') =>
    answers[role] ?? {};

  return (
    <div className={own.page}>
      <div className={own.card}>
        <Wordmark />
        <h1 className={page.title}>A few details about {form.data?.orgName}</h1>
        <p className={own.lead}>
          This is everything we need before we can send your agreement. It takes about five
          minutes. <strong>Nothing has to be finished in one go</strong> — save as you go and
          come back to it, and the link will still work.
        </p>

        {failure && (
          <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
            {isCallFailure(failure) ? failure.message
              : failure instanceof Error ? failure.message : 'That did not save.'}
          </div>
        )}

        {/*
          * ASKED FIRST, AND ASKED AT ALL, BECAUSE THE ALTERNATIVE IS A GUESS.
          *
          * Everything below this is attributed to whoever fills it in, and the
          * only other ways to know who that was were to read a name out of an
          * email address or to assume the champion did it — and the champion,
          * as the section below says in its own words, is often not the person
          * filling this in.
          */}
        <section className={own.section}>
          <h2 className={page.sectionTitle}>About you</h2>
          <p className={own.why}>
            So we know who to thank, and who to come back to with a question. You do not
            have to be any of the people named below.
          </p>
          <div className={own.fields}>
            <div className={own.field}>
              <label htmlFor="sub-name">Your name</label>
              <input id="sub-name" className={own.input} maxLength={PERSON_NAME_MAX}
                autoComplete="name"
                value={answers.submitter?.name ?? ''}
                onChange={(e) => setSubmitter('name', e.target.value)} />
            </div>
            <div className={own.field}>
              <label htmlFor="sub-email">Your email</label>
              <input id="sub-email" className={own.input} type="email" maxLength={EMAIL_MAX}
                autoComplete="email"
                value={answers.submitter?.email ?? ''}
                onChange={(e) => setSubmitter('email', e.target.value)} />
            </div>
          </div>
        </section>

        <section className={own.section}>
          <h2 className={page.sectionTitle}>Your organization</h2>
          <p className={own.why}>
            The legal name is what appears on the agreement, which is not always the name
            people use day to day.
          </p>
          <div className={own.fields}>
            <div className={own.field}>
              <label htmlFor="org-legal">Full legal name</label>
              <input id="org-legal" className={own.input} maxLength={ORG_NAME_MAX}
                value={answers.organization?.legalName ?? ''}
                onChange={(e) => setOrg('legalName', e.target.value)} />
            </div>
            <div className={own.field}>
              <label htmlFor="org-address">Business address</label>
              <input id="org-address" className={own.input} maxLength={500}
                value={answers.organization?.address ?? ''}
                onChange={(e) => setOrg('address', e.target.value)} />
            </div>
            <div className={own.field}>
              <label htmlFor="org-locations">How many locations</label>
              <input id="org-locations" className={own.input} type="number" min={1} max={500}
                aria-describedby="org-locations-hint"
                value={answers.organization?.locationCount ?? ''}
                onChange={(e) => {
                  setSaved(false);
                  const n = e.target.value === '' ? null : Number(e.target.value);
                  setAnswers((a) => ({ ...a, organization: { ...a.organization, locationCount: n } }));
                }} />
              <span className={own.hint} id="org-locations-hint">
                Every site where staff will read policies. Staff numbers never affect
                anything — there is no per-person charge.
              </span>
            </div>
            <div className={own.field}>
              <label htmlFor="org-type">What kind of organization</label>
              <select id="org-type" className={own.input}
                value={answers.organization?.organizationType ?? ''}
                onChange={(e) => setOrg('organizationType', e.target.value)}>
                <option value="">Choose one</option>
                {ORGANIZATION_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className={own.field}>
              <label htmlFor="org-golive">When you would like to be running <span className={own.hint}>optional</span></label>
              <input id="org-golive" className={own.input} type="date"
                aria-describedby="org-golive-hint"
                value={answers.organization?.targetGoLiveDate ?? ''}
                onChange={(e) => setOrg('targetGoLiveDate', e.target.value)} />
              <span className={own.hint} id="org-golive-hint">
                A date you are working towards, not a commitment either way. Leave it
                empty if you do not know yet — a guess is worse than nothing here,
                because we would plan around it.
              </span>
            </div>
          </div>
        </section>

        {PEOPLE.map((role) => (
          <section className={own.section} key={role.key}>
            <h2 className={page.sectionTitle}>{role.title}</h2>
            <p className={own.why}>{role.why}</p>
            <div className={own.fields}>
              <div className={own.field}>
                <label htmlFor={`${role.key}-name`}>Name</label>
                <input id={`${role.key}-name`} className={own.input} maxLength={PERSON_NAME_MAX}
                  value={person(role.key).name ?? ''}
                  onChange={(e) => setPerson(role.key, 'name', e.target.value)} />
              </div>
              <div className={own.field}>
                <label htmlFor={`${role.key}-email`}>Email</label>
                <input id={`${role.key}-email`} className={own.input} type="email" maxLength={EMAIL_MAX}
                  value={person(role.key).email ?? ''}
                  onChange={(e) => setPerson(role.key, 'email', e.target.value)} />
              </div>
              <div className={own.field}>
                <label htmlFor={`${role.key}-title`}>Job title</label>
                <input id={`${role.key}-title`} className={own.input} maxLength={JOB_TITLE_MAX}
                  value={person(role.key).title ?? ''}
                  onChange={(e) => setPerson(role.key, 'title', e.target.value)} />
              </div>
              <div className={own.field}>
                <label htmlFor={`${role.key}-phone`}>Phone</label>
                <input id={`${role.key}-phone`} className={own.input} maxLength={PHONE_MAX}
                  value={person(role.key).phone ?? ''}
                  onChange={(e) => setPerson(role.key, 'phone', e.target.value)} />
              </div>
            </div>
          </section>
        ))}

        <section className={own.section}>
          <h2 className={page.sectionTitle}>Anything your process requires</h2>
          <p className={own.why}>
            Every organization is different. Telling us now saves a week later — these are
            the things that most often hold a new supplier up.
          </p>
          {form.data && <OurDocs token={token} data={form.data} />}
          <div className={own.fields}>
            <div className={own.field}>
              <label htmlFor="req-po">Purchase order number, if you use one</label>
              <input id="req-po" className={own.input} maxLength={100}
                aria-describedby="req-po-hint"
                value={answers.requirements?.poNumber ?? ''}
                onChange={(e) => setReq('poNumber', e.target.value)} />
              <span className={own.hint} id="req-po-hint">
                Leave blank if you do not need one, or if it is not issued yet.
              </span>
            </div>
            <div className={own.field}>
              <label htmlFor="req-portal">Supplier portal we should register in</label>
              <input id="req-portal" className={own.input} maxLength={500}
                value={answers.requirements?.vendorPortal ?? ''}
                onChange={(e) => setReq('vendorPortal', e.target.value)} />
            </div>
          </div>

          <div className={own.checks}>
            <label className={own.check}>
              <input type="checkbox" checked={answers.requirements?.needsBaa === true}
                onChange={(e) => setReq('needsBaa', e.target.checked)} />
              <span>
                <strong>We need a business associate agreement</strong>
                <span className={own.hint}>
                  StandingDocs holds policy documents and records of who read them, not
                  patient information — so a BAA is usually not needed. Tick this if your
                  own policy requires one of every supplier and we will send ours.
                </span>
              </span>
            </label>
            <label className={own.check}>
              <input type="checkbox" checked={answers.requirements?.needsSecurityReview === true}
                onChange={(e) => setReq('needsSecurityReview', e.target.checked)} />
              <span>
                <strong>Our IT or security team will want to review this</strong>
                <span className={own.hint}>
                  We will send our security overview, which answers most questionnaires
                  before anybody has to fill one in.
                </span>
              </span>
            </label>
          </div>

          <div className={own.field}>
            <label htmlFor="req-notes">Anything else we should know</label>
            <textarea id="req-notes" className={own.input} rows={4} maxLength={2000}
              value={answers.requirements?.notes ?? ''}
              onChange={(e) => setReq('notes', e.target.value)} />
          </div>
        </section>

        <div className={own.actions}>
          <button type="button" className={page.btn} disabled={busy}
            onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : saved ? 'Saved' : 'Save and come back later'}
          </button>
          <button type="button" className={`${page.btn} ${page.btnPrimary}`} disabled={busy}
            onClick={() => submit.mutate()}>
            {submit.isPending ? 'Sending…' : 'Send this to StandingDocs'}
          </button>
        </div>
        {form.data && form.data.outstanding.length > 0 && (
          <p className={page.itemMeta}>
            Still to fill in: {form.data.outstanding.join(', ')}. You can send it once those
            are done.
          </p>
        )}
      </div>
    </div>
  );
}
